import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ChevronRight } from "lucide-react";
import { findSubcategory } from "../catalogue";
import { categories } from "../data";

export default function ProductDetail() {
  const { slug } = useParams();
  const sub = findSubcategory(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!sub) {
    return (
      <main className="section-padding py-32 text-center" data-testid="product-not-found">
        <p className="overline mb-4">/ ERROR / 404</p>
        <h1 className="h-display text-5xl md:text-6xl mb-6">Product line not found.</h1>
        <p className="font-mono text-sm text-textmuted mb-8">
          The catalogue entry you're looking for doesn't exist or has been moved.
        </p>
        <Link to="/products" className="btn-primary">
          Back to catalogue <ArrowLeft size={14} />
        </Link>
      </main>
    );
  }

  const parentCategory = categories.find((c) => c.slug === sub.category);

  return (
    <main data-testid="product-detail-page" className="pb-12">
      {/* Breadcrumb + Title */}
      <section className="border-b border-ink-700 section-padding pt-24 pb-16">
        <nav className="font-mono text-[11px] tracking-[0.24em] uppercase text-textmuted mb-6 flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
          <Link to="/products" className="hover:text-signal-amber transition-colors">/ catalogue</Link>
          <ChevronRight size={12} className="opacity-50" />
          <Link to={`/products#${sub.category}`} className="hover:text-signal-amber transition-colors">
            {parentCategory?.title || sub.category}
          </Link>
          <ChevronRight size={12} className="opacity-50" />
          <span className="text-signal-amber">{sub.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="overline mb-3">{parentCategory?.code} · LINE</p>
            <h1 className="h-display text-5xl md:text-7xl max-w-[18ch]" data-testid="product-detail-title">
              {sub.title}
              <span className="text-signal-amber">.</span>
            </h1>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-textmuted">
              {sub.products.length} model{sub.products.length === 1 ? "" : "s"} in this range
            </p>
          </div>
          <Link
            to={`/enquiry?product=${encodeURIComponent(`${parentCategory?.title || sub.category} · ${sub.title}`)}`}
            className="btn-primary self-start lg:self-end"
            data-testid="enquire-range"
          >
            Enquire about this range <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      {/* Product cards */}
      <section className="section-padding py-20 space-y-12 lg:space-y-20">
        {sub.products.map((p, idx) => (
          <ProductCard key={`${p.name}-${idx}`} product={p} index={idx} subTitle={sub.title} categoryTitle={parentCategory?.title || sub.category} reverse={idx % 2 === 1} />
        ))}
      </section>

      {/* Bottom CTA */}
      <section className="section-padding py-20 border-t border-ink-700 bg-ink-900/60">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="overline mb-3">// Need a quote</p>
            <h3 className="h-display text-3xl md:text-4xl max-w-[22ch]">
              Share quantities &amp; deadlines — we'll respond within one business day.
            </h3>
          </div>
          <Link
            to={`/enquiry?product=${encodeURIComponent(`${parentCategory?.title || sub.category} · ${sub.title}`)}`}
            className="btn-primary"
            data-testid="bottom-cta"
          >
            Send enquiry <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}

function ProductCard({ product, index, subTitle, categoryTitle, reverse }) {
  const enquiryUrl = `/enquiry?product=${encodeURIComponent(`${categoryTitle} · ${product.name}`)}`;
  return (
    <article
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 border border-ink-700 bg-ink-900/40"
      data-testid={`product-card-${index}`}
    >
      <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""} bg-white relative overflow-hidden`}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full max-h-[460px] object-contain p-8"
            loading="lazy"
          />
        ) : (
          <div className="w-full aspect-square flex items-center justify-center text-textmuted font-mono text-xs uppercase tracking-widest p-8">
            Image unavailable
          </div>
        )}
        <p className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-signal-amber bg-ink-950/80 px-2 py-1">
          {String(index + 1).padStart(2, "0")} / {subTitle}
        </p>
      </div>

      <div className={`lg:col-span-7 p-8 lg:p-12 flex flex-col ${reverse ? "lg:order-1" : ""}`}>
        <h2 className="font-display font-black text-2xl md:text-3xl lg:text-4xl leading-tight mb-4">
          {product.name}
        </h2>
        {product.description && (
          <p className="font-mono text-sm text-textmuted leading-relaxed mb-6">
            {product.description}
          </p>
        )}
        {product.bullets && product.bullets.length > 0 && (
          <ul className="space-y-3 mb-8">
            {product.bullets.map((b, i) => (
              <li key={i} className="flex gap-3 font-mono text-sm text-textmain leading-relaxed">
                <span className="text-signal-phosphor flex-shrink-0 select-none">▸</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto pt-6 flex flex-wrap gap-4">
          <Link to={enquiryUrl} className="btn-primary" data-testid={`enquire-${index}`}>
            Enquire / get quote <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}
